package com.snakesworld.game.domain

import com.snakesworld.game.engine.Direction
import com.snakesworld.game.engine.FoodPellet
import com.snakesworld.game.engine.GameConstants
import com.snakesworld.game.engine.SnakePlayer
import com.snakesworld.game.engine.SnakeSegment
import com.snakesworld.game.engine.Vector2D

/**
 * Simple playable bots for offline single-player: chase the nearest pellet,
 * keep 4-directional heading with 180° reversal lockout, and steer away from
 * walls and other snake bodies.
 */
object SnakeAiController {
    const val BOT_COUNT: Int = 3
    const val RESPAWN_DELAY_MS: Long = 3000L
    const val SPAWN_MIN_SEPARATION: Float = 280f

    val BOT_NAMES: List<String> = listOf("BOT_VIPER", "BOT_FANG", "BOT_DRAKE")

    fun chooseDirection(
        lastStepped: Direction,
        head: Vector2D,
        segments: List<SnakeSegment>,
        pellets: Collection<FoodPellet>,
        otherSnakes: Collection<SnakePlayer>,
        stepSize: Float = GameConstants.SEGMENT_SPACING,
        arenaHalfSize: Float = GameConstants.ARENA_HALF_SIZE,
    ): Direction {
        val candidates = Direction.entries.filter { SnakeKinematics.isValidTurn(lastStepped, it) }
        if (candidates.isEmpty()) return lastStepped

        val nearestPellet = pellets.minByOrNull { head.distanceSquaredTo(it.position) }
        val wallMargin = stepSize * 2f

        var best = lastStepped.takeIf { it in candidates } ?: candidates.first()
        var bestScore = Float.NEGATIVE_INFINITY

        for (dir in candidates) {
            val next = head + (dir.toVector() * stepSize)
            var score = 0f

            if (CollisionDetection.isOutOfBounds(next, arenaHalfSize - wallMargin)) {
                score -= 12_000f
            }
            if (CollisionDetection.isOutOfBounds(next, arenaHalfSize)) {
                score -= 20_000f
            }
            if (CollisionDetection.checkSelfCollision(next, segments)) {
                score -= 10_000f
            }
            for (other in otherSnakes) {
                if (CollisionDetection.checkOpponentBodyCollision(next, other.segments)) {
                    score -= 10_000f
                }
                if (CollisionDetection.checkHeadToHeadCollision(
                        next,
                        other.headPosition,
                    ) != HeadToHeadResult.NoCollision
                ) {
                    score -= 4_000f
                }
            }
            if (nearestPellet != null) {
                val currentDist = head.distanceSquaredTo(nearestPellet.position)
                val nextDist = next.distanceSquaredTo(nearestPellet.position)
                score += (currentDist - nextDist) / 10f
            }
            if (dir == lastStepped) {
                score += 8f
            }

            if (score > bestScore) {
                bestScore = score
                best = dir
            }
        }

        return best
    }
}
